import {Component} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {WorkflowService} from "../workflow.service";
import {AssetService} from "../../asset/asset.service";
import {AssetRemovalService} from "../../asset/asset-removal.service";
import {AccessoryService} from "../../accessory/accessory.service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";
import {WorkflowStep} from "../workflow-step";
import {DeploymentService} from "../../deployment/deployment.service";
import {DeploymentWorkflowService} from "./deployment-workflow.service";
import {SelectableAsset, selectableAssetModelListFactory} from "../../types/selectable-asset";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {addressDifferenceListFactory} from "../../address/address.model";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {DialogShowSiteDifferencesComponent, ShowSiteDiffDialogModel} from "./dialog-show-site-differences.component";
import {toggleLoading} from "../../misc/loading.decorator";
import {DeploymentGqlModel} from "../../deployment/deployment.gql-model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;
function tokenizeString(str, tokenizers = "\\s") {
  if (!str) return [];
  const regex = new RegExp(`(?:[^${tokenizers}"]+|"[^"]*")+`, "g");
  return str.match(regex);
}

function stripQuotes(str) {
  if (!str || !str.trim()) return "";
  const result = str.match(/^"(.*)"$/);
  if (result && result.length > 1) return result[1];
  return str;
}

function cleanParam(param = "") {
  const clean = ((typeof param) !== "string") ? `${param}` : param;
  return clean.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
}

@Component({
  templateUrl: "./deploy-devices.template.pug",
  providers: [
    DeploymentService,
    DeploymentWorkflowService,
    AssetService,
    AssetRemovalService,
    AccessoryService,
    DialogService,
  ],
})
export class DeployDevicesComponent extends WorkflowStep {
  siteId: string;
  deploymentId: string;
  error: Error;
  assets: SelectableAsset[] = [];
  filterAssets: SelectableAsset[] = [];
  displayAssets: SelectableAsset[] = [];
  deployment: DeploymentGqlModel = new DeploymentGqlModel({});
  siteDifferences: any[] = [];
  labelFrom: string = "K2/ACDC";

  assetStateList = [
    {key: "missing-information", text: "Missing or invalid asset details", color: "#6f6f12", icon: "warning"},
    {key: "ready", text: "Ready to upload", color: "#000000", icon: "none"},
    {key: "upload-failed", text: "Upload failed", color: "#6f1212", icon: "error"},
    {key: "exists-in-k2", text: "Device is in K2", color: "#0f590f", icon: "ok"},
    {key: "found-in-k2", text: "Device was found in K2", color: "#0f590f", icon: "ok"},
    {key: "removal-in-k2", text: "Device was removed from K2", color: "#0f590f", icon: "ok"},
    {key: "validated", text: "Dry Run Successful", color: "#000000", icon: "none"},
  ];

  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  searchText: string = "";

  hideDataFullAssets: boolean = true;
  hideCompletedAssets: boolean = true;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public deploymentService: DeploymentService,
    public deploymentWorkflowService: DeploymentWorkflowService,
    private dialogService: DialogService,
    public assetService: AssetService,
    public assetRemovalService: AssetRemovalService,
    public accessoryService: AccessoryService,
  ) {
    super(true);
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filterAssets.length / (+this.pageSize));
  }

  loadDeploymentData() {
    this.loading = false;
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.filterAssets = this.assets.filter((asset) => {
      if (this.searchText) {
        const values = [
          asset.id,
          asset.serialNumber,
          asset.assetTag,
          asset.building,
          asset.floor,
          asset.floorMapId,
          asset.productNumber,
        ];

        const searchFailures = tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => stripQuotes(v)) // strip quotes on ends if present
          .map(v => cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      if (this.hideDataFullAssets && asset.statusKey !== "missing-information") return false;
      if (
        this.hideCompletedAssets &&
        (
          asset.statusKey === "exists-in-k2" ||
          asset.statusKey === "found-in-k2" ||
          asset.statusKey === "removal-in-k2"
        )
      ) return false;

      return true;
    });
    this.displayAssets = this.filterAssets.slice(start, end);
    this.recalculateMaxPages();
  }

  @handleErrors()
  async edit(asset: SelectableAsset) {
    switch (asset.type) {
      case "asset": await this.assetService.editAsset(asset.id);
        break;
      case "asset removal": await this.assetRemovalService.editAsset(asset.id);
        break;
      case "accessory": await this.accessoryService.editAccessory(asset.id);
        break;
      default:
        throw new Error("Could not edit this item.");
    }

    this.loadData();
  }

  @toggleLoading()
  @handleErrors()
  async loadData(): Promise<void> {
    this.error = undefined;
    if (!this.deploymentId) return;

    this.loading = true;

    this.deploymentWorkflowService.getAssetList(this.deploymentId)
      .toPromise()
      .then(
        ({rows, differingSites}) => {
          this.assets = selectableAssetModelListFactory(rows, {});
          this.siteDifferences = addressDifferenceListFactory(differingSites);
          this.recalculateMaxPages();
          this.loading = false;
        },
        (error) => {
          this.assets = [];
          this.error = error;
          this.loading = false;
        },
      )
      .then(() => this.loadDeploymentData());

    this.deployment = await this.deploymentService.getDeploymentById(this.deploymentId);
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        break;
    }
    this.loadDeploymentData();
  }

  ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.deploymentId = workflowTargetInfo.targetId;
    this.loadData();
  }

  async viewDifferingSites() {
    await DialogShowSiteDifferencesComponent.showSiteDifferencesDialogModal(
      this.dialogService, {siteDifferences: this.siteDifferences} as ShowSiteDiffDialogModel,
    );
  }
}
